/**
 * API методы для работы с услугами клиники
 * Дата: 29.01.2026 - Создан для TODO-038 (Services Module)
 * Связан с: internal/handler/medical_service.go, frontend/src/hooks/useMedicalServices.ts, frontend/src/pages/Services.tsx
 */
import { apiClient } from './client';

export interface ServiceCategory {
  id: string;
  name: string;
  slug: string;
  description?: string;
  icon?: string;
  display_order: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface MedicalService {
  id: string;
  external_id: string;
  name: string;
  category_id?: string;
  source_system?: string;
  source_data?: Record<string, any>;
  is_active: boolean;
  last_synced_at?: string;
  created_at: string;
  updated_at: string;
  category?: ServiceCategory;
}

export interface ServiceTemplateLink {
  id: string;
  service_id: string;
  template_id: string;
  priority: number;
  delay_hours: number;
  frequency: 'once' | 'daily' | 'weekly';
  max_sends: number;
  conditions?: Record<string, any>;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  service?: MedicalService;
  template?: {
    id: string;
    name: string;
  };
}

export interface ServiceFilters {
  category_id?: string;
  // 16.03.2026 - УСЛУГИ: server-side фильтр по выбранному кластеру
  cluster_id?: string;
  search?: string;
  is_active?: boolean;
  source_system?: string;
  unlinked_only?: boolean;
  // 16.03.2026 - УСЛУГИ: server-side флаг "показать только вне кластеров"
  unclustered_only?: boolean;
}

export const medicalServicesApi = {
  // Categories
  listCategories: async (includeInactive = false): Promise<{ categories: ServiceCategory[] }> => {
    const response = await apiClient.get('/services/categories', {
      params: { include_inactive: includeInactive },
    });
    return response.data;
  },

  // Services
  list: async (filters?: ServiceFilters, offset = 0, limit = 20): Promise<{ services: MedicalService[]; total: number }> => {
    const params: Record<string, string> = {
      offset: offset.toString(),
      limit: limit.toString(),
    };
    if (filters?.category_id) params.category_id = filters.category_id;
    if (filters?.cluster_id) params.cluster_id = filters.cluster_id;
    if (filters?.search) params.search = filters.search;
    if (filters?.is_active !== undefined) params.is_active = filters.is_active.toString();
    if (filters?.source_system) params.source_system = filters.source_system;
    if (filters?.unlinked_only) params.unlinked_only = 'true';
    if (filters?.unclustered_only) params.unclustered_only = 'true';

    const response = await apiClient.get('/services', { params });
    return response.data;
  },

  get: async (id: string): Promise<MedicalService> => {
    const response = await apiClient.get(`/services/${id}`);
    return response.data;
  },

  create: async (data: Partial<MedicalService>): Promise<MedicalService> => {
    const response = await apiClient.post('/services', data);
    return response.data;
  },

  update: async (id: string, data: Partial<MedicalService>): Promise<MedicalService> => {
    const response = await apiClient.put(`/services/${id}`, data);
    return response.data;
  },

  // Service Templates (привязки)
  linkTemplate: async (serviceId: string, data: {
    template_id: string;
    priority?: number;
    delay_hours?: number;
    frequency?: 'once' | 'daily' | 'weekly';
    max_sends?: number;
    conditions?: Record<string, any>;
    is_active?: boolean;
  }): Promise<ServiceTemplateLink> => {
    const response = await apiClient.post(`/services/${serviceId}/templates`, data);
    return response.data;
  },

  unlinkTemplate: async (serviceId: string, templateId: string, delayHours = 0): Promise<void> => {
    await apiClient.delete(`/services/${serviceId}/templates/${templateId}`, {
      params: { delay_hours: delayHours.toString() },
    });
  },

  getServiceTemplates: async (serviceId: string): Promise<{ templates: ServiceTemplateLink[] }> => {
    const response = await apiClient.get(`/services/${serviceId}/templates`);
    return response.data;
  },

  // Batch endpoint для загрузки привязок сразу для нескольких услуг (оптимизация N+1)
  // Дата: 29.01.2026 - TODO-038 РЕВЬЮ: добавлено для оптимизации
  getBatchServiceTemplates: async (serviceIds: string[]): Promise<{ templates_by_service: Record<string, ServiceTemplateLink[]> }> => {
    const response = await apiClient.post('/services/templates/batch', {
      service_ids: serviceIds,
    });
    return response.data;
  },

  // Дата: 05.02.2026 - TODO-055: Статистика использования услуг по годам
  getUsageStats: async (offset = 0, limit = 50): Promise<{ services: ServiceUsageStats[]; total: number }> => {
    const response = await apiClient.get('/services/usage-stats', {
      params: { offset: offset.toString(), limit: limit.toString() },
    });
    return response.data;
  },
};

// Дата: 05.02.2026 - TODO-055: Статистика использования услуги
export interface ServiceUsageStats {
  service_id: string;
  service_name: string;
  service_code: string;
  total_usage: number;
  usage_2023: number;
  usage_2024: number;
  usage_2025: number;
  usage_2026: number;
  last_used_at?: string;
  is_active: boolean;
  category_id?: string;
  category_name?: string;
  linked_template: boolean;
}
